/**
 * Support Stage — Stage 5: Support/hint validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.6
 *
 * Validates:
 * - Families that require hints have them available
 * - Support coverage meets policy requirements
 *
 * ENHANCED (GAP-P4): Now checks FAMILY_SUPPORT_REGISTRY for actual hint presence,
 * not just emitting a generic info note.
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { coach, info } from '../issueContract'
import { getSupportPolicy, isPolicyFeatureEnabled } from '../policyEngine'
import { FAMILY_SUPPORT_REGISTRY } from '../../../support'
import { QUESTIONS_BY_FAMILY, type Question } from '../../../content'
import {
  analyzeNumberPatterns,
  detectNumberPatternCollapse,
} from '../../utils/numberPatternAnalysis'
import {
  analyzeLinguisticVariation,
  detectLinguisticTemplateCollapse,
  getLinguisticVariationSuggestions,
} from '../../utils/linguisticVariationAnalysis'

const STAGE: PipelineStage = 5

/**
 * Run support validation on a question.
 */
export function runSupportStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  if (!question.familyId) return issues
  if (!isPolicyFeatureEnabled('enforceSupportCoverage')) return issues

  const supportPolicy = getSupportPolicy(question.familyId)
  if (!supportPolicy) return issues

  // ── Hints required check (GAP-P4: now validates against actual registry) ─

  if (supportPolicy.hintsRequired) {
    const registryEntry = FAMILY_SUPPORT_REGISTRY[question.familyId]
    const hasRegistryHints =
      registryEntry?.hints && registryEntry.hints.length > 0

    if (!registryEntry) {
      // Family not in registry at all — more serious
      issues.push(
        coach(
          'support',
          STAGE,
          'SUPPORT_FAMILY_NOT_IN_REGISTRY',
          `Family "${question.familyId}" requires hints but is not in FAMILY_SUPPORT_REGISTRY`,
          {
            questionId: qId,
            familyId: fId,
            severity: 40,
            suggestion:
              'Add this family to FAMILY_SUPPORT_REGISTRY with appropriate hints',
          },
        ),
      )
    } else if (!hasRegistryHints) {
      // Family in registry but has no hints
      issues.push(
        coach(
          'support',
          STAGE,
          'SUPPORT_HINTS_EMPTY',
          `Family "${question.familyId}" is in registry but has no hints defined`,
          {
            questionId: qId,
            familyId: fId,
            severity: 35,
            suggestion:
              'Add hints array with at least one HintStep to the registry entry',
          },
        ),
      )
    }
  }

  // ── defaultExplanation quality check ─────────────────────────────────────
  // Validates that families in the registry have a meaningful defaultExplanation.
  // Checks: presence, minimum length (20 chars), maximum length (500 chars).

  {
    const registryEntry = FAMILY_SUPPORT_REGISTRY[question.familyId]
    if (registryEntry) {
      const explanation = registryEntry.defaultExplanation?.trim() ?? ''

      if (!explanation) {
        issues.push(
          coach(
            'support',
            STAGE,
            'SUPPORT_EXPLANATION_MISSING',
            `Family "${question.familyId}" is in registry but has no defaultExplanation`,
            {
              questionId: qId,
              familyId: fId,
              severity: 35,
              suggestion:
                'Add a defaultExplanation string that explains the concept in 1-3 sentences suitable for groep 6/7/8',
            },
          ),
        )
      } else if (explanation.length < 20) {
        issues.push(
          coach(
            'support',
            STAGE,
            'SUPPORT_EXPLANATION_TOO_SHORT',
            `Family "${question.familyId}" has a very short defaultExplanation (${explanation.length} chars) — likely incomplete`,
            {
              field: 'defaultExplanation',
              expected: '≥20 chars',
              actual: `${explanation.length} chars`,
              questionId: qId,
              familyId: fId,
              severity: 30,
              suggestion:
                'Expand the defaultExplanation to at least 20 characters with a clear, child-friendly explanation of the concept',
            },
          ),
        )
      } else if (explanation.length > 500) {
        issues.push(
          info(
            'support',
            STAGE,
            'SUPPORT_EXPLANATION_TOO_LONG',
            `Family "${question.familyId}" has a long defaultExplanation (${explanation.length} chars) — may be hard to read for groep 6/7/8`,
            {
              field: 'defaultExplanation',
              expected: '≤500 chars',
              actual: `${explanation.length} chars`,
              questionId: qId,
              familyId: fId,
              severity: 15,
              suggestion:
                'Consider shortening the defaultExplanation to ≤500 characters for better readability',
            },
          ),
        )
      }
    }
  }

  // ── Explanation required check ───────────────────────────────────────────

  if (supportPolicy.explanationRequired) {
    const hasExplanation = question.feedback?.explanationText?.trim()
    if (!hasExplanation) {
      issues.push(
        coach(
          'support',
          STAGE,
          'SUPPORT_EXPLANATION_REQUIRED',
          `Family "${question.familyId}" requires an explanation in feedback`,
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: 50,
          },
        ),
      )
    }
  }

  // ── Visual support required check ────────────────────────────────────────

  if (supportPolicy.visualSupportRequired) {
    const hasVisualSupport =
      Array.isArray(question.supportAids) && question.supportAids.length > 0
    if (!hasVisualSupport) {
      issues.push(
        coach(
          'support',
          STAGE,
          'SUPPORT_VISUAL_REQUIRED',
          `Family "${question.familyId}" requires visual support aids`,
          {
            field: 'supportAids',
            questionId: qId,
            familyId: fId,
            severity: 45,
          },
        ),
      )
    }
  }

  // ── Family question count check (CAL-004) ──────────────────────────────
  // Emit info-level warning when a question's family has fewer than 5 questions.
  // This helps identify families that need additional content authoring.

  {
    const familyQuestions = QUESTIONS_BY_FAMILY.get(question.familyId)
    const familyCount = familyQuestions?.length ?? 0
    if (familyCount < 5) {
      issues.push(
        info(
          'support',
          STAGE,
          'SUPPORT_FAMILY_LOW_QUESTION_COUNT',
          `Family "${question.familyId}" has only ${familyCount} question(s) — minimum 5 recommended for adequate practice coverage`,
          {
            field: 'familyId',
            expected: '≥5',
            actual: String(familyCount),
            questionId: qId,
            familyId: fId,
            severity: 20,
            suggestion: `Author additional questions for family "${question.familyId}" to reach at least 5 for balanced practice sessions`,
          },
        ),
      )
    }

    // ── NEW: citoDifficulty spread within family (F3) ──────────────────────
    // Check if all questions in a family have identical citoDifficulty values.
    // Only emit once per family (on the first question that has citoDifficulty).
    if (
      familyQuestions &&
      familyQuestions.length >= 3 &&
      question.citoDifficulty !== undefined
    ) {
      const citoDiffs = familyQuestions
        .map((q) => q.citoDifficulty)
        .filter((d): d is number => d !== undefined && typeof d === 'number')

      if (citoDiffs.length >= 3) {
        const allSame = citoDiffs.every((d) => d === citoDiffs[0])
        if (allSame) {
          // Only emit on the first question in the family to avoid spam
          if (familyQuestions[0]?.id === question.id) {
            issues.push(
              info(
                'support',
                STAGE,
                'SUPPORT_FAMILY_CITO_NO_SPREAD',
                `All ${citoDiffs.length} questions in family "${question.familyId}" have identical citoDifficulty (${citoDiffs[0]}) — spread recommended for balanced assessment`,
                {
                  field: 'citoDifficulty',
                  expected: 'spread ≥ 0.10',
                  actual: `all ${citoDiffs[0]}`,
                  questionId: qId,
                  familyId: fId,
                  severity: 25,
                  suggestion:
                    'Vary citoDifficulty across questions (e.g., 0.25-0.55) to create a mix of easier and harder items',
                },
              ),
            )
          }
        } else {
          // Check if spread is too narrow (< 0.10)
          const minDiff = Math.min(...citoDiffs)
          const maxDiff = Math.max(...citoDiffs)
          const spread = maxDiff - minDiff
          if (spread < 0.1 && familyQuestions[0]?.id === question.id) {
            issues.push(
              coach(
                'support',
                STAGE,
                'SUPPORT_FAMILY_CITO_LOW_SPREAD',
                `Family "${question.familyId}" has insufficient citoDifficulty spread (${spread.toFixed(2)}, range: ${minDiff}-${maxDiff}) — all questions are too similar in difficulty`,
                {
                  field: 'citoDifficulty',
                  expected: 'spread ≥ 0.10',
                  actual: `spread ${spread.toFixed(2)}`,
                  questionId: qId,
                  familyId: fId,
                  severity: 35,
                  suggestion:
                    'Add both "instap" questions (lower difficulty) and "uitdaging" questions (higher difficulty) to create a learning progression within the family',
                },
              ),
            )
          }
        }
      }
    }

    // ── NEW: Number pattern collapse check (F6) ─────────────────────────────
    // Detect "model collapse" where AI uses only "nice" numbers (10, 20, 1/2).
    // Only relevant for math-heavy modules: GET, REK, MET, TIJD, GELD.
    // Emit once per family (on first question).
    const mathModules = [
      'Getalbegrip',
      'Basisrekenen',
      'Metriek',
      'Tijd',
      'Geld',
    ]
    if (
      familyQuestions &&
      familyQuestions.length >= 5 &&
      familyQuestions[0]?.id === question.id &&
      question.module &&
      mathModules.includes(question.module)
    ) {
      const analysis = analyzeNumberPatterns(
        familyQuestions.map((q) => ({
          question: q.question ?? '',
          options: q.options,
        })),
      )

      const collapse = detectNumberPatternCollapse(analysis)
      if (collapse.issue) {
        issues.push(
          info(
            'support',
            STAGE,
            'SUPPORT_FAMILY_NUMBER_PATTERN_COLLAPSE',
            `Family "${question.familyId}" shows number pattern collapse: ${collapse.issue}`,
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: collapse.severity,
              suggestion:
                'Add questions with "ugly" numbers (13, 27, 3.47), edge cases (0, 1), and overflow situations (18+5) to prevent model collapse',
            },
          ),
        )
      }
    }

    // ── NEW: Linguistic variation check (F7) ─────────────────────────────────
    // Detect "template collapse" where AI uses repetitive formulations.
    // Relevant for ALL modules (not just math).
    // Emit once per family (on first question).
    if (
      familyQuestions &&
      familyQuestions.length >= 5 &&
      familyQuestions[0]?.id === question.id
    ) {
      const lingAnalysis = analyzeLinguisticVariation(
        familyQuestions.map((q) => ({
          question: q.question ?? '',
        })),
      )

      const lingCollapse = detectLinguisticTemplateCollapse(lingAnalysis)
      if (lingCollapse.issue) {
        const suggestions = getLinguisticVariationSuggestions(lingAnalysis)
        issues.push(
          info(
            'support',
            STAGE,
            'SUPPORT_FAMILY_LINGUISTIC_TEMPLATE_COLLAPSE',
            `Family "${question.familyId}" shows linguistic template collapse: ${lingCollapse.issue}`,
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: lingCollapse.severity,
              suggestion: suggestions.join(' • '),
            },
          ),
        )
      }
    }
  }

  return issues
}
